import type { SVGProps } from 'react'
import { useSessionUser } from '@/hooks/use-session'
import { useLingui } from '@lingui/react/macro'
import { Link } from '@tanstack/react-router'
import { useAppStore } from '@/lib/app-store'
import { cn } from '@/lib/utils'

/** The raw SVG logo — use this when you only need the icon without a link wrapper. */
export const LogoIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg {...props} height={20} width={164} viewBox="0 0 263 32">
    <path
      fill="rgb(var(--text))"
      d="M45.672 1.524h5.788l12.231 28.741h-6.568l-2.609-6.399H42.451l-2.609 6.399h-6.399zm6.604 16.796-3.787-9.254-3.792 9.262zM67.23 1.729h5.788L86.389 19.3V1.729h6.195v28.535h-5.338l-13.819-18.14v18.14H67.23zm30.398 14.349v-.081c0-8.114 6.115-14.759 14.88-14.759 5.389 0 8.602 1.793 11.252 4.402l-3.996 4.607c-2.201-1.997-4.444-3.22-7.297-3.22-4.809 0-8.275 3.994-8.275 8.886v.082c0 4.884 3.383 8.969 8.275 8.969 3.262 0 5.259-1.305 7.501-3.343l3.994 4.042c-2.935 3.139-6.195 5.096-11.7 5.096-8.398-.007-14.634-6.488-14.634-14.683zm30.38-14.349h6.278v11.298h11.577V1.729h6.278v28.535h-6.278V18.813h-11.577v11.451h-6.278zm30.756 0h6.278v28.535h-6.278zm22.087-.205h5.788l12.231 28.741h-6.568l-2.609-6.399h-12.068l-2.609 6.399h-6.399zm6.605 16.796-3.791-9.262-3.792 9.262zm14.951-16.591h5.788L221.566 19.3V1.729h6.195v28.535h-5.34l-13.816-18.14v18.14h-6.199zm30.398 14.349v-.081c0-8.112 6.399-14.757 15.207-14.757s15.124 6.568 15.124 14.676v.082c0 8.112-6.399 14.757-15.207 14.757s-15.124-6.566-15.124-14.678zm23.766 0v-.081c0-4.884-3.587-8.969-8.642-8.969s-8.561 3.994-8.561 8.886v.082c0 4.884 3.587 8.969 8.642 8.969s8.563-3.996 8.563-8.888z"
    />
    <path
      fill="rgb(var(--color-primary))"
      d="m33.214 16.1 6.202-14.575h-5.173L28.041 16.1zM-.01 1.525h10.717c9.033 0 15.274 6.2 15.274 14.287v.077c0 8.083-6.241 14.37-15.274 14.37H-.01zm10.717 24.142c6.035 0 9.976-4.065 9.976-9.69v-.088c0-5.625-3.941-9.767-9.976-9.767H5.042v19.544z"
    />
  </svg>
)

export function Logo(props: { className?: string; openInNewTab?: boolean }) {
  const { t } = useLingui()
  const customize = useAppStore((state) => state.store.customize)
  const user = useSessionUser()

  if (customize.disableLogo) return

  if (customize.loginUrl) {
    return (
      <a
        href={customize.loginUrl ?? window.location.origin}
        target={customize.loginUrl && props.openInNewTab ? '_blank' : undefined}
        rel={customize.loginUrl && props.openInNewTab ? 'noopener noreferrer' : undefined}
        title={user ? t`Start` : t`Login`}
        className={cn('p-5', props.className)}
      >
        <LogoIcon />
      </a>
    )
  }

  return (
    <Link
      to={user ? (user.type === 'applicant' ? '/report' : '/employees') : '/login'}
      search={(prev) => prev}
      title={user ? t`Start` : t`Login`}
      className={cn('p-3 -ml-3', props.className)}
    >
      <LogoIcon />
    </Link>
  )
}
